import "./extensions/StringExtensions.js";
import "./extensions/SetExtensions.js";
import "./extensions/MapExtensions.js";
import "./extensions/ArrayExtensions";
import baseMerge from "lodash/_baseMerge.js";
import BigNumber from "bignumber.js";

let id_counter = 0;

/**
 * Statische Utility-Klasse zu den Themen Objekte und Collections
 */
export default class Commons {

    /**
     * führt ein schnelles, stabiles sort mit frei definierbarer Comparator-Funktion wie bei array.sort aus aus,
     * allerdings nicht in-place, d.h. gibt eine sortierte Kopie zurück. Lodash Sort kann dagegen leider keine
     * Comparator-Funktion mit (a,b), sondern nur mit einem Property - facepalm
     */
    static stableSort(array, comparator = (l,r) => l-r ) {
        return array
            .map((element, index) => ({element, index}))
            .sort((a, b) => comparator(a.element, b.element) || a.index - b.index)
            .map(entry=>entry.element);
    }

    /**
     * Sortiert einen azyklischen gerichteten Graphen, d.h. gibt eine Liste zurück,
     * in der Abhängigkeiten der Reihenfolge nach aufgelöst sind.
     *
     * Beispiel: wenn man ein Array von Aufgaben hat, und Aufgaben können voneinander abhängen,
     * dann kriegt man eine Reihenfolge zurück, in der die Aufgaben hinter einander abgearbeitet werden können.
     * Edges muss in diesem Beispiel zu einer Aufgabe A alle Aufgaben zurück geben,
     * die als Vorbedingung erledigt werden müssen (es reichen die direkten Vorgänger-Aufgaben).
     *
     * @see https://www.tutorialspoint.com/Topological-sorting-using-Javascript-DFS
     * @param array
     * @param edges ein Callback das zu jedem element die referenzierten Elemente zurückgibt
     * @return {Array}
     */
    static topologicalSort(array, edges = (element) => []) {
        let sorted = [];
        let visited = new Set();
        const visit = (element) => {
            if (visited.has(element)) {
                return;
            }
            visited.add(element);
            edges(element).forEach(visit);
            sorted.push(element);
        };
        array.forEach(visit);
        return sorted;
    }

    /**
     * Erweitert ein Objekt mit neuen Methoden per MonkeyPatching.
     * Achtung! Getter/Setter statt dessen mit monkeyProperty zuweisen!
     *
     * @example
     * monkeyPatch(String.prototype,{ sayHelloWorld: ()=>'Hello World'; })
     * "x".sayHelloWorld()=='Hello World'
     */
    static monkeyPatch(instanceToPatch, literalWithMethods) {
        Object.keys(literalWithMethods).forEach(key => {
            if (!instanceToPatch[key]) {
                instanceToPatch[key] = literalWithMethods[key];
            }
        });
    }

    /**
     * Erweitert ein Objekt um eine neue Property-Methode
     */
    static monkeyProperty(instanceToPatch, propertyName, getset) {
        if (typeof instanceToPatch[propertyName] === 'undefined') {
            Object.defineProperty(instanceToPatch, propertyName, getset);
        }
    }

    /**
     * Ruft n mal valueOrFunction auf und gibt das Ergebnis als Array zurück.
     * valueOrFunction kann entweder eine Funktion(index,n) sein oder einfach ein Wert wie 'Hallo'.
     * @param n
     * @param valueOrFunction
     * @return {Array}
     */
    static repeatAsArray(n, valueOrFunction) {
        let creator = _.isFunction(valueOrFunction) ? valueOrFunction : function () {
            return _.cloneDeep(valueOrFunction);
        };
        let array = [];
        for (let i = 0; i < n; i++) {
            array.push(creator(i, n));
        }
        return array;
    }

    /**
     * Setzt die Anzahl der Elemente eines Arrays auf den Parameter count, und löscht oder erzeugt dazu Elemente.
     * @param array
     * @param count
     * @param createElementCallback
     * @param removeElementCallback
     * @return {Array}
     */
    static setArrayLength(array,count,createElementCallback,removeElementCallback=undefined) {
        while (count>array.length) {
            array.push(createElementCallback(array.length));
        }
        if (array.length>count) {
            if (removeElementCallback) {
                for (let i=count;i<array.length;i++) {
                    removeElementCallback(array[i]);
                }
            }
            array.splice(count,array.length-count);
        }
        return array;
    }

    /**
     * Ähnlich wie array.map, aber verwendet vorhandene Objekte wieder.
     * @param sourceArray
     * @param destinationArray
     * @param createElementCallback
     * @param mapCallback
     * @returns {Array}
     * @example
     * Statt:
     * this.destinationArray = sourceArray.map(source=>new Destination().fromSource(source));
     * Schreibt man:
     * this.destinationArray = Commons.mapreuse(sourceArray,this.destinationArray,source=>new Destination(),(destination,source)=>destination.fromSource(source));
     * Dadurch wird das destinationArray und darin enthaltene Objekte wiederverwendet, und nur bei Bedarf Objekte neu erzeugt oder gelöscht.
     * mapCallback wird für jedes Objekt aufgerufen.
     */
    static mapreuse(sourceArray,destinationArray,createElementCallback,mapCallback=()=>{}) {
        if (sourceArray==null) {
            return sourceArray;
        }
        if (destinationArray==null) {
            destinationArray = [];
        }
        const sourceArrayLength = sourceArray.length;
        while (sourceArrayLength>destinationArray.length) {
            destinationArray.push(createElementCallback(sourceArray[destinationArray.length]));
        }
        if (destinationArray.length>sourceArrayLength) {
            destinationArray.splice(sourceArrayLength,destinationArray.length-sourceArrayLength);
        }
        destinationArray.forEach((a,i)=>{
            destinationArray[i] = mapCallback(a,sourceArray[i]);
        });
        return destinationArray;
    }

    /**
     * Entfernt Duplicate aus einem Array, benutzt intern als Vergleich IsEqualComparer(object,object)
     * @param array
     * @param isSorted
     * @return {Array}
     */
    static unique(array, isSorted = false) {
        return isSorted ? _.sortedUniq(array) : _.uniq(array);

    }

    /**
     * Fügt einem Array alle Werte anderer Arrays hinzu (modifiziert das erste Array).
     * @param {Array} targetArray
     * @param {...Array} valuesToAddArrays
     * @return {Array}
     */
    static addAll(targetArray, ...valuesToAddArrays) {
        if (!_.isArray(targetArray)) {
            throw "targetArray must be an array";
        }
        valuesToAddArrays.forEach(valuesToAdd => {
            if (valuesToAdd == null) {
                return;
            }
            if (!_.isArray(valuesToAdd)) {
                throw "valuesToAdd must be an array";
            }
            Array.prototype.push.apply(targetArray, valuesToAdd);
        });
        return targetArray;
    }

    /**
     * Permutationsvergleich: Testet, ob zwei Arrays anhand einer Vergleichsfunktion (morphism) die gleichen Werte enhalten,
     * wobei die Reihenfolge nicht wichtig ist.
     * @return {boolean}
     */
    static areArraysIsomorph(morphism, array1, array2) {
        if (array1.length !== array2.length) {
            return false;
        }
        const length = array1.length;
        const visited = Array.from(array1).map(_=>false);
        outer: for( let i = 0; i<length;i++){
            for (let j= 0; j<length;j++){
               const targetIndex=(i+j)%length;
               if (!visited[targetIndex] && morphism(array1[i],array2[targetIndex])){
                   //wir haben Match gefunden
                   visited[targetIndex]=true;
                   continue outer;
               }
            }
            //innere Schleife durchgelaufen ohne Match
            return false;
        }
        //äußere Schleife durchlaufen, und immer einen Match gefunden
        return true;
    }

    /**
     * Zählt die Anzahl der Elemente, auf welche die predicate - Funktion zutrifft.
     * @return {number}
     */
    static count(array, predicate) {
        if (array==null) {
            return 0;
        }
        let c = 0;
        for (let i = 0; i < array.length; i++) {
            if (predicate(array[i])) {
                c++;
            }
        }
        return c;
    }

    /**
     * gibt das Minimum eines Arrays, bezogen auf eine Sortierfunktion zurück
     * @deprecated verwende _.minBy
     */
    static minBy(array, orderMapping) {
        return _.minBy(array, orderMapping);
    }

    /**
     * gibt den Index des ersten Min-Elementes eines Arrays zurück, bezogen auf eine Abbildung auf die reelen Zahlen zurück, oder -1, falls das Array leer ist.
     * @return {*}
     */
    static minIndexBy(array, orderNumberFunction=x=>x) {
        return array.indexOf(_.minBy(array,orderNumberFunction));
    }

    /**
     * gibt den Index des ersten Max-Elementes eines Arrays zurück, bezogen auf eine Abbildung auf die reelen Zahlen zurück, oder -1, falls das Array leer ist.
     * @return {*}
     */
    static maxIndexBy(array, orderNumberFunction=x=>x) {
        return array.indexOf(_.maxBy(array,orderNumberFunction));
    }

    /**
     * Summiert alle Werte eines Array. Leere Arrays ergeben null.
     */
    static sum(array = []) {
        let v = 0;
        for (let i = 0; i < array.length; i++) {
            v += array[i] || 0;
        }
        return v;
    }

    /**
     * Multipliziert alle Werte eines Arrays. Leere Arrays ergeben 1.
     */
    static product(array) {
        return _.reduce(array, function (product, n) {
            return product * n;
        }, 1);
    }

    /**
     * Verzögert den Aufruf einer Funktion, bis predicateWaitingForToBecomeTrue true wird oder bis maxWaitTime
     * erreicht ist, falls > 0
     */
    static delayUntil(functionDelayed, predicateWaitingForToBecomeTrue, maxWaitTime = 0, pollIntervall = 100, onTimeout = () => {
    }) {
        let start = Date.now();

        function waitOnce() {
            if (predicateWaitingForToBecomeTrue()) {
                functionDelayed();
            } else if (maxWaitTime == 0 || Date.now() < start + maxWaitTime) {
                _.delay(waitOnce, pollIntervall);
            } else {
                onTimeout();
            }
        }

        waitOnce();
    }

    /**
     * Ruft einen Constructor mit einem Argumentarray auf.
     * @see http://nullprogram.com/blog/2013/03/24/
     */
    static invokeConstructor(constructorFunction, argumentArray) {
        let constructorArguments = [constructorFunction];
        addAll(constructorArguments, argumentArray);
        let factory = constructorFunction.bind.apply(constructorFunction, constructorArguments);
        return new factory();
    }

    /**
     * Ruft ein Callback für jeden Eintrag eines Arrays (value,index) oder eines Objektes (value,key) auf.
     */
    static forArrayOrObject(object, callback) {
        if (_.isArray(object)) {
            object.forEach((value, index) => callback(value, index, object.length));
        } else {
            let keys = _.sortBy(_.keys(object));
            keys.forEach(key => callback(object[key], key, keys.length));
        }
    }

    /**
     * Prüft im Gegensatz zu lodash / ramda auch auf Leerstrings, null, undefined, leere Arrays und Objekte ohne
     * Properties
     */
    static isEmpty(value) {
        return value === null
            || typeof value === 'undefined'
            || value === ''
            || value.length === 0
            || value.size === 0
            || (!Array.isArray(value) && typeof value === 'object' && Object.keys(value).length === 0);
    }

    /**
     * prüft zwei beliebige Objekte auf Gleichheit.
     * Dabei werden enthaltene Floats, Vectoren, Quaternionen, Box3 und weitere auf Minimaldistanz hin überprüft
     * statt auf exakter Gleichheit wegen eventueller Floating-Point-Differenzen.
     * Speziell für Vectoren, Quaternionen, Matrix wird mit der dortigen isEqualEpsilon verglichen,
     * weil nur so im Rahmen eines allgemeinen Vergleichs sicher zu stellen ist, dass Callback-Member-Variablen wie
     * onChangeCallback Teil des Vergleichs sind
     *
     * @param a Vergleichsobjekt 1
     * @param b Vergleichsobjekt 2
     * @param epsilon Erlaubte Floatingpointvarianz
     * @param allowIsomophArrays Beachte die reihenfolge bei Arrays nicht
     * @returns {boolean}
     */
    static isEqualEpsilon(a,b,epsilon=0.000001,allowIsomophArrays=false) {
        return _.isEqualWith(a,b,(a,b)=>{
            if (a!=null && a.isEqualEpsilon!==undefined && a.isEqualEpsilon(b,epsilon)) {
                return true;
            }
            if (a==b) {
                return true;
            }
            if (_.isNumber(a) && _.isNumber(b)) {
                return Math.abs(a-b)<epsilon;
            }
            if (allowIsomophArrays && Array.isArray(a) && Array.isArray(b)){
                return Commons.areArraysIsomorph( (x,y)=>Commons.isEqualEpsilon(x,y,epsilon,allowIsomophArrays),a,b );
            }
            return undefined;
        });
    }

    /**
     * wrapp func in eine neue Funktion, welche die Ein-/Ausgabe loggt.
     * @param args
     */
    static logCall(funcName,func) {
        return (...args) => {
            console.log('> '+funcName,args);
            const result = func(...args);
            console.log('< '+funcName+' result = ',result);
            return result;
        };
    }

    /**
     * Kapselt eine Funktion so, dass sie gecached wird, d.h. bei gleichen Aufrufparametern
     * wird die Funktion nur einmal aufgerufen, ansonsten der Wert des Caches zurückgegeben.
     * Die Aufrufparameter müssen sich als String konkatinieren lassen, weil der Key des
     * Caches ein String ist.
     * @example
     * const add = Commons.cache((a)=>{
     *    console.log('aufruf '+a);
     *    return a+1
     * });
     * add(1);
     * // ergibt 2 und logausgabe 'aufruf 1'
     * add(1);
     * // ergibt 2 und keine logausgabe
     * @template {() => any} T
     * @param {T} valueFactory
     * @returns {T}
     */
    static cache(valueFactory) {
        let map = new Map();
        let cacheID = Commons.CACHE_ID;
        return function (...args) {
            if (cacheID!=Commons.CACHE_ID) {
                cacheID = Commons.CACHE_ID;
                map.clear();
            }
            const key = Commons.cacheKey(...args);
            if (map.has(key)) {
                return map.get(key);
            }
            const value = valueFactory(...args);
            map.set(key, value);
            return value;
        }
    }

    static CACHE_ID=0;

    /**
     * hiermit werden alle mittels Commons.cache erzeugten Caches invalidiert
     */
    static resetCaches() {
        Commons.CACHE_ID++;
        console.log('Commons.resetCaches()');
    }

    /**
     * Wandelt ein Array von Objekten oder Primitiven in einen eindeutigen primitiven Wert um, meist ein String.
     * @returns {String}
     */
    static cacheKey(...args) {
        if (args.length == 0) {
            return null;
        }
        if (args.length == 1 && typeof args[0] !== 'object') {
            return args[0];
        }
        return Commons.cacheKeyIntern(args);
    }

    /**
     * @private
     */
    static cacheKeyIntern(arg) {
        if (arg == null) {
            return null;
        }
        const type = typeof arg;
        if (type === 'object') {
            // FIXME Reihenfolge der Serialisierung ist evtl. nicht eindeutig :-((
            return JSON.stringify(arg);
            // return JSON.stringify(arg, Object.keys(arg).sort());
        }
        if (type === 'function') {
            throw 'a function element is not supported as part of a cacheKey';
        }
        return arg;
    }

    /**
     * ruft eine Funktion solange auf, bis sie false zurückliefert
     */
    static iterateWhileDirty(callback) {
        let dirty;
        do {
            dirty = callback();
        } while (dirty);
    }

    /**
     * Iteriert über alle Properties eines verschachtelten Objektes und klopft es auf
     * eine Ebene flach, wobei Keys mit Punkt concatiniert werden.
     * @return {Object}
     * @example
     * flattenObject({
     *  'a': 1,
     *  b: { 'c': 2, 'd': 3  }
     * });
     * // ergibt
     * { 'a':1, 'b.c': 2, 'b.d': 3 }
     */
    static flattenObject(obj) {
        if (obj instanceof THREE.Vector3) {
            return {x: obj.x, y: obj.y, z: obj.z};
        }
        if (obj instanceof THREE.Vector2) {
            return {x: obj.x, y: obj.y};
        }
        if (obj instanceof THREE.Quaternion) {
            return {x: obj.x, y: obj.y, z: obj.z, w: obj.w};
        }
        let toReturn = {};
        for (let i in obj) {
            if (!obj.hasOwnProperty(i)) {
                continue;
            }
            if ((typeof obj[i]) == 'object') {
                let flatObject = this.flattenObject(obj[i]);
                for (let x in flatObject) {
                    if (!flatObject.hasOwnProperty(x)) {
                        continue;
                    }
                    toReturn[i + '.' + x] = flatObject[x];
                }
            } else {
                toReturn[i] = obj[i];
            }
        }
        return toReturn;
    }

    /**
     * Umkehrfunktion zu flattenObjekt
     * @return {Object}
     * @example
     * unflattenObject({ 'a':1, 'b.c': 2, 'b.d': 3 });
     * // ergibt
     * {
     *  'a': 1,
     *  b: { 'c': 2, 'd': 3  }
     * }
     */
    static unflattenObject(obj) {
        let toReturn = {};
        for (let i in obj) {
            if (!obj.hasOwnProperty(i)) {
                continue;
            }
            _.set(toReturn, i, obj[i]);
        }
        return toReturn;
    }

    /**
     * <strong>Statt lodash.defaultsDeep sollte stets Commons.defaultsDeepSafe verwendet werden.</strong>
     * Grund: lodash.defaultsDeep({object: ein3dObject},{object: anderes3dObject}) setzt ein3dObject.children =
     * anderes3dObject.children falls ein3dObject.children leer ist, was den Szene-Graph komplett zerhaut (zwei Objekte
     * haben dann dasselbe Child, welches aber nur ein Parent hat). Eigentlich sollte sowas ja klar sein, aber falls
     * man so blöde ist wie ich und sich deswegen stundenlang einen Wolf sucht, wird lodash.defaultsDeep
     * gefährlich. defaultsDeepSafe ist nicht neu programmiert sondern erweitert lodash.defaultsDeep so, dass nicht mehr zwei
     * Nicht-Konfigurationsobjekte gemergt werden. Konfigurationsobjekte sind solche mit anonymer Klasse (Plain
     * Objects) wie {a:'Hallo',b:'Welt'}. Bei allen anderen Objekten - wie THREE.Object3D - wird nur die Referenz
     * ausgetauscht. Die Methode entspricht so eher der intuitiven Erwartung.
     * @example
     * // Konkrete Fälle, wodefaultsDeepSafe von lodash.defaultsDeep abweicht:
     * defaultsDeepSafe({ a: Vector(1,1,1) }, { a: Vector(2,2,2) } ) => { a: Vector(1,1,1) }​ // hier wird nichts gemergt, nur die Referenz ausgetauscht
     * defaultsDeepSafe({ a: Vector(1,1,1) }, { a: {x:2} } ) => { a: Vector(1,1,1) } // hier wird nichts gemergt, nur die Referenz ausgetauscht
     * // Folgendes Verhalten ist dagegen in beiden Implementierungen gleich:
     * defaultsDeepSafe({ a: {x:1} }, { a: Vector(2,2,2) } ) => { a: Vector(1,2,2) } // hier wird gemergt, weil das erste ein Konfigurationsobjekt ist
     * // In allen anderen Fällen ist das Ergebnis gleich.
     */
    static defaultsDeepSafe(...args) {
        args.push(undefined, Commons._safeDefaultsDeepCustomizer);
        return _.mergeWith.apply(undefined, args);
    }

    /**
     * wie _.merge, aber Safe (defaultsDeepSafe)
     * @param args
     */
    static mergeSafe(...args) {
        return _.mergeWith(...args,Commons._safeDefaultsDeepCustomizer);
    }

    static _safeDefaultsDeepCustomizer(objValue, srcValue, key, object, source, stack) {
        if (_.isObject(objValue) && _.isObject(srcValue)) {
            if (!_.isPlainObject(objValue) && !_.isPlainObject(srcValue)) {
                if (_.isFunction(objValue.clone)) {
                    return objValue.clone();
                }
                return objValue;
            }
            baseMerge(objValue, srcValue, undefined, Commons._safeDefaultsDeepCustomizer, stack.set(srcValue, objValue));
        }
        return objValue;
    }

    /**
     * Diese Methode ist wie lodash.cloneDeep, aber alle Objekte, welche eine clone - Methode haben, werden
     * darüber gecloned, was auf fast alle ThreeJS-Objekte zutrifft.
     */
    static cloneDeep(value) {
        return _.cloneDeepWith(value, v => {
            if (v != null && _.isFunction(v.clone)) {
                return v.clone();
            }
        });
    }

    /**
     * Gibt für ein Source-Verzeichnis eine Map Klassenname zu Klasse zurück.
     * @example
     * Commons.slurpDirectory(require.context('./modelTypes/',true));
     */
    static slurpDirectory(requireContext) {
        let result = {};
        requireContext.keys().forEach(filepath => {
            const module = requireContext(filepath);
            const clazz = module.__esModule && module.default ? module.default : module;
            result[filepath.substring(filepath.lastIndexOf('/') + 1).split('.')[0]] = clazz;
        });
        return result;
    }

    /**
     * Erzeugt eine interne eindeutige ID als etwa 12-stelligen String zur Basis 64. Diese ID setzt sich aus der aktuellen Millisekunde (modulo 20 Jahre)
     * und einer 9-stelligen Zufallszahl zusammen und kann als global eindeutig im Rahmen einer Konfigurator-App über alle Benutzer angesehen werden.
     * @example
     * Commons.createUniqueId() => "yi$cX5xeX$M2"
     */
    static createUniqueIdString() {
        const date = new BigNumber(Date.now() % (20 * 365 * 24 * 60 * 60 * 1000)).shift(9);
        const random = new BigNumber(Math.random() + '').shift(9).ceil();
        return date.plus(random).toString(64).reverse();
    }

    /**
     * Erzeugt eine interne eindeutige ID als Zahl, die nicht zur Serialisierung taugt, aber im Rahmen
     * einer Browsersitzung der App eindeutig ist.
     * @example
     * Commons.createId() => 0
     */
    static createId() {
        return id_counter++;
    }

    /**
     * setzt die id zurück auf 0. Vorsicht!! Falls man createId() für Arrangement-Objekte benutzt, sollte man
     * danach auch APP.representer.reset() aufrufen.
     */
    static resetId() {
        id_counter=0;
    }


    /**
     * gibt den Typ bzw. Klassennamen eines Objektes zurück
     */
    static typeName(object) {
        if (object == null) {
            return 'null';
        }
        if (object.TYPENAME_UNMANGLED) {
            return object.TYPENAME_UNMANGLED;
        }
        if (object instanceof THREE.Object3D && !Commons.isEmpty(object.name)) {
            return object.name;
        }
        if (object.type && THREE.hasOwnProperty(object.type)) {
            return object.type;
        }

        const constructor_name = _.get(object, 'constructor.name');
        if (!Commons.isEmpty(constructor_name)) {
            return constructor_name;
        }
        if (object.type) {
            return object.type;
        }
        let funcNameRegex = /function (.{1,})\(/;
        let results = (funcNameRegex).exec((object).constructor.toString());
        if (results && results.length > 1 && !results[1] == 'onRotationChange') {
            return results[1];
        }
        return $.type(object);
    }

    /**
     * Wie _.partition, aber mit index als 2. Parameter, wie es halt sinn macht... (kopfschüttel): https://github.com/lodash/lodash/issues/2641
     * @param list
     * @param predicate
     */
    static partition(list, predicate) {
        const a = [];
        const b = [];
        list.forEach((element, index) => (predicate(element, index) ? a : b ).push(element));
        return [a, b];
    }

    /**
     * Wie _.groupBy, aber mit index als 2. Parameter, und mit Map als ergebnis, wie es halt sinn macht... (kopfschüttel): https://github.com/lodash/lodash/issues/2641
     * @param list
     * @param segmentCallback
     */
    static groupBy(list, segmentFn) {
        const map = new Map();
        list.forEach((element, index) => {
            const segment = segmentFn(element, index);
            let l = map.get(segment);
            if (l == null) {
                l = [];
                map.set(segment, l);
            }
            l.push(element);
        });
        return map;
    }

    /**
     * Wie groupBy, aber kann sowohl keys als auch values transformieren, und fügt
     * Werte unique und flat ein
     * @param list
     * @param key
     * @param values
     * @return {Map<any, any>}
     */
    static groupByFlat(list,key=(x,index)=>x,values=(x,index)=>x) {
        const map = new Map();
        list.forEach((x, index)=>{
            const k = key(x,index);
            const v = values(x,index);
            let set = map.get(k);
            if (set==null) {
                set = new Set();
                map.set(k,set);
            }
            if (Array.isArray(v)) {
                v.forEach(x=>set.add(v));
            } else {
                set.add(v);
            }
        });
        map.forEach(((values,key)=>{
            const a = values.toArray();
            map.set(key,a);
        }));
        return map;
    }

    /**
     * Gruppiert die Elemente eines Arrays in ein Array von Segment-Arrays gleichen Wertes von segmentFn.
     * @param list das zu segmentierende Array
     * @param segmentFn optional mappt das Element auf einen Segment-Key. Default ist die Identität
     * @returns {Array}
     * @example
     * Commons.segments([1,1,1,2,2,2,1,1,1,1,3,3,5]) => [[1,1,1],[2,2,2],[1,1,1,1],[3,3],[5]]
     */
    static segments(list, segmentFn = (x) => x) {
        const segments = [];
        if (list && list.length > 0) {
            let current = null;
            let lastKey = undefined;
            list.forEach((element, index) => {
                const currentKey = segmentFn(element, index);
                if (lastKey !== currentKey) {
                    current = [];
                    segments.push(current);
                }
                current.push(element);
                lastKey = currentKey;
            });
        }
        return segments;
    }

    /**
     * Iteriert über eine Liste und übergibt an den Iterator die Info, ob es das erste oder letzte Elemente ist
     * @param list
     * @param iterator
     */
    static forEachFirstLast(list, iterator = (element, index, isFirst, isLast) => {
    }) {
        const last = list.length - 1;
        list.forEach((element, index) => {
            const isFirst = index == 0;
            const isLast = index == last;
            iterator(element, index, isFirst, isLast);
        });
    }


    /**
     * Mapt eine Liste und übergibt an die Map-Funktion die Info, ob es das erste oder letzte Elemente ist
     * @param list
     * @param mapper
     */
    static mapFirstLast(list, mapper = (element, index, isFirst, isLast) => {
    }) {
        const last = list.length - 1;
        return list.map((element, index) => {
            const isFirst = index == 0;
            const isLast = index == last;
            return mapper(element, index, isFirst, isLast);
        });
    }

    /**
     * Wie _.intersectionBy, allerdings wird als erster Parameter ein Array von Array erwartet, statt einer Parameterliste von Arrays.
     * @param array
     * @param segmentFn
     */
    static intersectionBy(array, segmentFn) {
        const args = _.clone(array);
        args.push(segmentFn);
        return _.intersectionBy.apply(this, args);
    }

    /**
     * geht alle Paar-Kombinationen eines Arrays mit sich selbst durch.
     * Beispiel: [a,b,c] liefert die Aufrufkette (a,b),(a,c),(b,c)
     * @param array
     * @param callback mit signatur (element1,element2,index1,index2)
     */
    static pairCombinations(array, callback) {
        const l = array.length;
        for (let i = 0; i < l; i++) {
            for (let j = i + 1; j < l; j++) {
                callback(array[i], array[j], i, j);
            }
        }
    }

    /**
     * Wie JSON.stringify, kommt aber mit rekursiven Datenstrukturen zurecht. Praktisch für Logausgaben.
     */
    static stringifyOnce(obj, replacer, indent) {
        let printedObjects = [];
        let printedObjectKeys = [];

        function printOnceReplacer(key, value) {
            if (printedObjects.length > 2000) { // browsers will not print more than 20K, I don't see the point to allow 2K.. algorithm will not be fast anyway if we have too many objects
                return 'object too long';
            }
            let printedObjIndex = false;
            printedObjects.forEach(function (obj, index) {
                if (obj === value) {
                    printedObjIndex = index;
                }
            });

            if (key == '') { //root element
                printedObjects.push(obj);
                printedObjectKeys.push("root");
                return value;
            }

            else if (printedObjIndex + "" != "false" && typeof(value) == "object") {
                if (printedObjectKeys[printedObjIndex] == "root") {
                    return "(pointer to root)";
                } else {
                    return "(see " + ((!!value && !!value.constructor) ? value.constructor.name.toLowerCase() : typeof(value)) + " with key " + printedObjectKeys[printedObjIndex] + ")";
                }
            } else {

                let qualifiedKey = key || "(empty key)";
                printedObjects.push(value);
                printedObjectKeys.push(qualifiedKey);
                if (replacer) {
                    return replacer(key, value);
                } else {
                    return value;
                }
            }
        }

        return JSON.stringify(obj, printOnceReplacer, indent);
    }

    /**
     * Pendant zur serverseitigen PHP-Funktion Helper::safeFileName. Somit können z.B. normalisierte Dateinamen abgefragt werden.
     */
    static safeFileName(name) {
        return name ? name.replace(/[\/\\:~&#=\'`\+]+/ig, '').replace(/\s\s/g, ' ') : null;
    }

    /**
     * Wie Object.getOwnPropertyNames, aber auch für vererbte Properties der Super-Klassen.
     * @param {Object} obj
     * @returns {Array<string>}
     */
    static getAllPropertyNames(obj) {
        let props = [];
        do {
            props = props.concat(Object.getOwnPropertyNames(obj));
        } while (obj = Object.getPrototypeOf(obj));
        return props;
    }

    /**
     * Gibt alle Methoden eines Objektes zurück, die mit einem bestimmten Prefix anfangen
     * @param {Object} obj
     * @param {string} prefix
     * @returns {Object}
     */
    static getPrefixedMethods(obj, prefix) {
        const methods = {};
        Commons.getAllPropertyNames(obj)
            .filter(key => key.startsWith(prefix))
            .forEach(key => methods[key.substringAfter(prefix)] = (...args) => obj[key](...args));
        return methods;
    }

    /**
     * Zählt die Anzahl der Parameter, die nicht empty sind
     * @returns {number}
     */
    static countNotEmpty(...parameter) {
        let count = 0;
        for (let i = 0; i < parameter.length; i++) {
            if (!Commons.isEmpty(parameter[i])) {
                count += 1;
            }
        }
        return count;
    }

    /**
     * vergleicht zwei Objektgraphen und gibt das Delta aus
     * @param a
     * @param b
     * @return {{different: Array, missing_from_first: Array, missing_from_second: Array}}
     */
    static deepDiff(a, b, equal = (a,b,key)=>Commons.isEqualEpsilon(a,b)) {

        const result = {
            different: [],
            missing_from_first: [],
            missing_from_second: []
        };

        _.reduce(a, (result, value, key) => {
            if (b!=null && b.hasOwnProperty(key)) {
                if (equal(value, b[key], key)) {
                    return result;
                } else {
                    if (typeof (a[key]) != typeof ({}) || typeof (b[key]) != typeof ({})) {
                        //dead end.
                        result.different.push(key);
                        return result;
                    } else {
                        const deeper = Commons.deepDiff(a[key], b[key], equal);
                        result.different = result.different.concat(_.map(deeper.different, (sub_path) => {
                            return key + "." + sub_path;
                        }));

                        result.missing_from_second = result.missing_from_second.concat(_.map(deeper.missing_from_second, (sub_path) => {
                            return key + "." + sub_path;
                        }));

                        result.missing_from_first = result.missing_from_first.concat(_.map(deeper.missing_from_first, (sub_path) => {
                            return key + "." + sub_path;
                        }));
                        return result;
                    }
                }
            } else {
                result.missing_from_second.push(key);
                return result;
            }
        }, result);

        _.reduce(b, (result, value, key) => {
            if (a!=null && a.hasOwnProperty(key)) {
                return result;
            } else {
                result.missing_from_first.push(key);
                return result;
            }
        }, result);

        return result;
    }

    /**
     * Prüft ob zwei Objektgraphen einen Unterschied haben (wenn einen das Delta nicht interessiert)
     * @param a
     * @param b
     * @param equal
     * @returns {boolean}
     */
    static hasDeepDiff(a,b,equal){
        const deepDiff=Commons.deepDiff(a,b,equal);
        return [deepDiff.different,deepDiff.missing_from_first,deepDiff.missing_from_second].some(x=>x.length>0);
    }

    /**
     * Löscht item aus array (mutiert array)
     * @param array
     * @param item
     * @return {*}
     */
    static remove(array,item) {
        const index = array.indexOf(item);
        if (index>=0) {
            array.splice(index,1);
        }
        return array;
    }

    /**
     * Liefert zu einem Event-Namen einen Methoden-Namen, wie "selectElement" => "onSelectElement"
     * @param event
     * @return {string}
     */
    static getMethodNameForEvent(event) {
        return 'on'+event.charAt(0).toUpperCase() + event.slice(1);
    }

    /**
     * Commons.pairwise([1]) => []
     * Commons.pairwise([1,2,3]) => [[1,2],[2,3]]
     * @param array
     * @param size
     */
    static pairwise(array,size=2) {
        if (array==null) {
            return [];
        }
        const pairs = [];
        for (let i=0;i<array.length-size+1;i++) {
            pairs.push(array.slice(i,i+size));
        }
        return pairs;
    }

    /**
     * gibt einen Proxy zurück, mit dem man wie in einer Breitensuche durch eine Objektstruktur navigieren kann.
     * Mit .exitProxy() verlässt man den Proxy und erhält ein Array zurück.
     * Null oder Undefined Einträge werden rausgefiltert.
     *
     * Beispiel: Commons.pathProxy(mein).Auto.Räder.Radmuttern.exitProxy() liefert ein Array von allen Radmuttern des Autos und ist eine
     * Kurzschreibweise für _.uniq((mein.Auto||[]).flatMap(x=>x.Räder.flatMap(x=>x.Radmuttern)).filter(x=>x!=null))
     *
     * @param objectOrArray
     */
    static pathProxy(objectOrArray) {
        const toArray = (objectOrArray) => {
            if (objectOrArray==null) {
                return [];
            } else if (Array.isArray(objectOrArray)) {
                return objectOrArray;
            } else if (objectOrArray instanceof Set) {
                return Array.from(objectOrArray);
            }
            return [objectOrArray];
        }
        const proxy = (array) => new Proxy(array, {
            get(array, prop) {
                if (prop=='exitProxy') {
                    return ()=>_.uniq(array).filter(x=>x!=null).sort((a, b) => a.Sortierung - b.Sortierung);
                }
                return proxy(array.flatMap(x=>{
                    if (x==null) {
                        return [];
                    }
                    if (x.adjacency && prop in x.adjacency) {
                        return toArray(x.adjacency[prop]);
                    }
                    return toArray(x[prop]);
                }));
            }
        });
        return proxy(toArray(objectOrArray));
    }


    /**
     * gibt eine Function zurück, welche für jeden Eintrag aufgerufen wird -
     * bei einem Array/Set für jedes Element, sonst für den übergebenen Wert.
     * @param objectOrArray
     * @return {(function(*): *)|(function(*=): void)}
     */
    static forOneOrEach(objectOrArray) {
        if (Array.isArray(objectOrArray) || objectOrArray instanceof Set) {
            return (callback)=>objectOrArray.forEach(callback);
        }
        return callback => callback(objectOrArray);
    }

    /**
     * schläft eine bestimmte Zeit, kann mittels await aufgerufen werden
     * @param ms
     * @returns {Promise<unknown>}
     */
    static sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

